import { forwardRef } from 'react';
import { Chip } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import { type QuizSetDetailsRes } from '@/api';
import { type CheckMyQuizStatus } from '@/hooks/useCheckMyQuiz';
import { WithPlanBadge } from '@/shared/components/Badge/PlanBadge';
import { type PlanType } from '@/types';
import { type MapTemplateType, MapTemplateTypeLocaleKey } from '@/types/MapTemplate';
import { cn } from '@/utils';
import { HeroContent } from './components/HeroCard/components/HeroContent/HeroContent';
import { HeroImage } from './components/HeroCard/components/HeroImage/HeroImage';
import { useHeroCardData } from './components/HeroCard/hooks/useHeroCardData';
export type QuizDetailHeroCardProps = {
  quizData: QuizSetDetailsRes;
  meStatus: CheckMyQuizStatus;
  className?: string;
};
export const QuizDetailHeroCard = forwardRef<HTMLDivElement, QuizDetailHeroCardProps>(({
  quizData,
  meStatus,
  className
}, ref) => {
  const {
    t
  } = useTranslation(['common']);
  // Transform data using hook
  const heroCardData = useHeroCardData(quizData, meStatus);
  return <div ref={ref}>
      {/* Mobile Layout (< tablet-sm) */}
      <div className={cn('flex w-full flex-col gap-2xs tablet-sm:hidden', className)}>
        {/* Mobile Hero Image + Type Badge */}

        <div className="relative flex w-full items-end justify-between">
          <WithPlanBadge isBadgeVisible={quizData.templatePlanType !== 'FREE'} planType={quizData.templatePlanType as Exclude<PlanType, 'FREE'>}>
            <HeroImage data={heroCardData.image} className="rounded-md outline outline-[4px] outline-white" />
          </WithPlanBadge>
          {/* Mobile Type Badge (순서 풀이) */}
          <div className="mb-2xs flex shrink-0 items-end pb-2xs">
            <Chip size="sm" className="rounded-xs border border-alternative bg-neutral px-xs py-3xs text-caption-md-semibold">
              {t(MapTemplateTypeLocaleKey[heroCardData.image.type as MapTemplateType])}
            </Chip>
          </div>
        </div>

        {/* Mobile Hero Content (Card Style) */}
        <div className="flex w-full flex-col gap-sm rounded-md border border-opacity-assistive bg-white p-md shadow-md">
          <HeroContent data={heroCardData} />
        </div>
      </div>

      {/* Desktop Layout (>= tablet-sm) */}

      <div className={cn('hidden w-full items-start gap-xl rounded-md border border-opacity-assistive bg-white p-lg px-xl py-lg shadow-md tablet-sm:flex', className)}>
        <WithPlanBadge isBadgeVisible={quizData.templatePlanType !== 'FREE'} planType={quizData.templatePlanType as Exclude<PlanType, 'FREE'>}>
          {/* Desktop Hero Image */}
          <HeroImage data={heroCardData.image} className="rounded-md" />
        </WithPlanBadge>

        {/* Desktop Hero Content */}
        <HeroContent data={heroCardData} />
      </div>
    </div>;
});
QuizDetailHeroCard.displayName = 'QuizDetailHeroCard';